// src/pages/super-admin/Clients/ClientDetails.tsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  useDeactivateClientMutation,
  useActivateClientMutation,
  useGetClientByIdQuery,
  useUpdateClientMutation,
  type ClientRequest
} from "@/api/clientApi";

import { useRequestPasswordResetMutation } from "@/api/authApi";
import ClientUsersSection from "./ClientUsersSection";
import ClientMarginManagement from "../components/ClientMarginManagement";
import ClientReportsSection from "./ClientReportsSection";
import { Users, Settings, Mail, Building2, UserRound, Phone, MapPin, Loader2 } from "lucide-react";

export default function ClientDetails() {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdParam = params.id; // Route parameter is :id, not :clientId

  // Validate and parse clientId
  if (!clientIdParam) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 mb-2">Client ID Missing</h2>
              <p className="text-gray-600 mb-4">
                No client ID provided in the URL.
              </p>
              <p className="text-sm text-gray-500">
                Expected URL format: /super-admin/clients/[number]
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const clientId = Number(clientIdParam);
  console.log('ClientDetails - params:', params, 'clientIdParam:', clientIdParam, 'clientId:', clientId);

  if (isNaN(clientId) || clientId <= 0) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 mb-2">Invalid Client ID</h2>
              <p className="text-gray-600 mb-4">
                The client ID "{clientIdParam}" is not a valid number. Please check the URL and try again.
              </p>
              <p className="text-sm text-gray-500">
                Expected URL format: /super-admin/clients/[number]
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // At this point, clientId is guaranteed to be a valid number (> 0)
  const validClientId = clientId;

  // ✅ FIX: Don't block on error, just get what we can
  const { data: client, isLoading: clientLoading, error: clientError, refetch } =
    useGetClientByIdQuery(validClientId);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<ClientRequest>({
    companyName: "",
    ein: "",
    contactEmail: "",
    contactPhone: "",
    businessAddress: "",
    city: "",
    state: "",
    zipCode: "",
    pocName: "",
  });

  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation();
  const [deactivateClient, { isLoading: isDeactivating }] = useDeactivateClientMutation();
  const [activateClient] = useActivateClientMutation();
  const [requestPasswordReset, { isLoading: isResetRequesting }] = useRequestPasswordResetMutation();


  useEffect(() => {
    if (isEditOpen && client) {
      setEditForm({
        companyName: client.companyName || "",
        ein: client.ein || "",
        contactEmail: client.contactEmail || "",
        contactPhone: client.contactPhone || "",
        businessAddress: client.businessAddress || "",
        city: client.city || "",
        state: client.state || "",
        zipCode: client.zipCode || "",
        pocName: client.pocName || "",
        marginStrategy: client.marginStrategy || "CLIENT_SPECIFIC",
      });
    }
  }, [isEditOpen, client]);

  useEffect(() => {
    if (client && searchParams.get("edit") === "1") {
      setIsEditOpen(true);

      // Clean up the URL so refresh/back behavior is nicer.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("edit");
        return next;
      }, { replace: true });
    }
  }, [client, searchParams, setSearchParams]);

  const canDeactivate = useMemo(() => {
    return client?.status !== "INACTIVE";
  }, [client?.status]);

  const handleSave = async () => {
    try {
      if (!editForm.companyName.trim() || !editForm.contactEmail.trim()) {
        toast.error("Company name and contact email are required");
        return;
      }
      await updateClient({ id: validClientId, data: editForm }).unwrap();
      toast.success("Client updated");
      setIsEditOpen(false);
      refetch();
    } catch (err) {
      toast.error("Failed to update client");
    }
  };

  const handleDeactivate = async () => {
    if (!canDeactivate) return;
    const ok = window.confirm("Deactivate this client? All client users/admins will be deactivated.");
    if (!ok) return;
    try {
      await deactivateClient(validClientId).unwrap();
      toast.success("Client deactivated");
      refetch();
    } catch {
      toast.error("Failed to deactivate client");
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await activateClient(id).unwrap();
      toast.success("Client reactivated successfully");
      refetch();
    } catch {
      toast.error("Failed to reactivate client");
    }
  };



  if (clientLoading) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Loading client details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if client doesn't exist
  if (clientError || !client) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 mb-2">Client Not Found</h2>
              <p className="text-gray-600 mb-4">
                The client with ID "{clientId}" was not found. It may have been deleted or you may not have permission to view it.
              </p>
              <Button onClick={() => window.history.back()}>
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ✅ Use fallback values if client doesn't exist
  const clientName = client?.companyName || `Client #${validClientId}`;
  const clientEmail = client?.contactEmail || "Not registered yet";

  return (
    <div className="p-6 space-y-6">
      {/* Client Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{clientName}</h1>
          <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1">
            <Mail className="w-3.5 h-3.5" />
            {clientEmail}
          </p>
          {!client && (
            <p className="text-xs text-amber-600 mt-2">
              ⚠️ Client not yet registered in system
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setIsEditOpen(true)} disabled={!client}>
            Edit
          </Button>

          <Button
            variant={client?.status === "INACTIVE" ? "default" : "destructive"}
            onClick={client?.status === "INACTIVE" ? () => handleActivate(validClientId) : handleDeactivate}
            disabled={!client || isDeactivating}
            className={client?.status === "INACTIVE" ? "bg-emerald-600 hover:bg-emerald-700 font-semibold" : "font-semibold"}
          >
            {client?.status === "INACTIVE" ? "Activate Client" : "Deactivate Client"}
          </Button>
        </div>
      </div>

      {/* Client Information Summary */}
      {client && (
        <Card className="bg-slate-50/50 border-slate-200 shadow-sm">
          <CardHeader className="py-3 bg-white border-b border-slate-100">
            <CardTitle className="text-sm font-bold text-slate-600 uppercase tracking-widest flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-600" />
              Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Point of Contact</p>
                <div className="flex items-center gap-1.5 text-sm text-slate-700 font-medium">
                  <UserRound className="w-3.5 h-3.5 text-blue-500" />
                  {client.pocName || "Not set"}
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Phone Number</p>
                <div className="flex items-center gap-1.5 text-sm text-slate-700 font-medium">
                  <Phone className="w-3.5 h-3.5 text-blue-500" />
                  {client.contactPhone || "Not set"}
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Tax ID (EIN)</p>
                <div className="flex items-center gap-1.5 text-sm text-slate-700 font-medium">
                  <Building2 className="w-3.5 h-3.5 text-blue-500" />
                  {client.ein || "Not set"}
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Address</p>
                <div className="flex items-start gap-1.5 text-sm text-slate-700 font-medium">
                  <MapPin className="w-3.5 h-3.5 text-blue-500 mt-0.5" />
                  <span>
                    {client.businessAddress}
                    {(client.city || client.state || client.zipCode) && (
                      <span className="block text-xs font-normal text-slate-500 mt-0.5">
                        {[client.city, client.state, client.zipCode].filter(Boolean).join(", ")}
                      </span>
                    )}
                    {!client.businessAddress && !client.city && "Not set"}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Company Name *</label>
              <Input
                value={editForm.companyName}
                onChange={(e) => setEditForm((p) => ({ ...p, companyName: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">EIN</label>
              <Input
                value={editForm.ein || ""}
                onChange={(e) => setEditForm((p) => ({ ...p, ein: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">POC Name</label>
              <Input
                value={editForm.pocName || ""}
                onChange={(e) => setEditForm((p) => ({ ...p, pocName: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Email *</label>
              <Input
                type="email"
                value={editForm.contactEmail}
                onChange={(e) => setEditForm((p) => ({ ...p, contactEmail: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Phone</label>
              <Input
                value={editForm.contactPhone || ""}
                onChange={(e) => setEditForm((p) => ({ ...p, contactPhone: e.target.value }))}
              />
            </div>
            <div className="pt-2 border-t mt-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Admin Security</p>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-blue-600 border-blue-200 hover:bg-blue-50"
                disabled={isResetRequesting}
                onClick={async () => {
                  if (confirm("Send password reset link to client contact email?")) {
                    try {
                      await requestPasswordReset({ email: editForm.contactEmail }).unwrap();
                      toast.success("Password reset email sent successfully");
                    } catch (err: any) {
                      toast.error(err.data?.message || "Failed to send reset email");
                    }
                  }
                }}
              >
                {isResetRequesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send Reset Email
              </Button>
            </div>
            <div>
              <label className="text-sm font-medium">Business Address</label>
              <Input
                value={editForm.businessAddress || ""}
                onChange={(e) => setEditForm((p) => ({ ...p, businessAddress: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium">City</label>
                <Input
                  value={editForm.city || ""}
                  onChange={(e) => setEditForm((p) => ({ ...p, city: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">State</label>
                <Input
                  value={editForm.state || ""}
                  onChange={(e) => setEditForm((p) => ({ ...p, state: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">ZIP</label>
                <Input
                  value={editForm.zipCode || ""}
                  onChange={(e) => setEditForm((p) => ({ ...p, zipCode: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Margin Strategy</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={editForm.marginStrategy || "CLIENT_SPECIFIC"}
                onChange={(e) => setEditForm((p) => ({ ...p, marginStrategy: e.target.value }))}
              >
                <option value="CLIENT_SPECIFIC">Client-Specific (Editable Overrides)</option>
                <option value="UNIFORM">Uniform (Read-Only Overrides)</option>
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">
                Uniform strategy disables clinic-level margin overrides for this client.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isUpdating}>
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tabs for Employees, Margins, Users, and Reports */}
      <Tabs defaultValue="employees" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="employees" className="gap-2">
            <Users className="h-4 w-4" />
            User Management
          </TabsTrigger>
          <TabsTrigger value="margins" className="gap-2">
            <Settings className="h-4 w-4" />
            Margins
          </TabsTrigger>
          {/* <TabsTrigger value="reports" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Lab Reports
          </TabsTrigger> */}
          {/* <TabsTrigger value="users" className="gap-2">
            <FileText className="h-4 w-4" />
            Users
          </TabsTrigger> */}
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees" className="mt-6">
          <ClientUsersSection clientId={validClientId} />
        </TabsContent>

        {/* Margins Tab */}
        <TabsContent value="margins" className="mt-6">
          <ClientMarginManagement clientId={validClientId} />
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-6">
          <ClientReportsSection clientId={validClientId} />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <Card>
            <CardHeader className="flex justify-between items-center">
              <CardTitle>Users</CardTitle>
              <Button>+ Add User</Button>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">No users found</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
